import { useState, useEffect } from 'react';
import { AppLayout } from './AppLayout';
import { ApiService } from '../services/apiService';
import { ModelValidationRun, ModelValidationResult } from '../types/modelValidation';
import { Play, RefreshCw, CheckCircle, XCircle, Clock, Loader2, Copy } from 'lucide-react';

export const ModelValidationPage = () => {
  const [runs, setRuns] = useState<ModelValidationRun[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [retestingModel, setRetestingModel] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const apiService = ApiService.getInstance();

  useEffect(() => {
    loadRuns();
  }, []);

  const loadRuns = async () => {
    setIsLoadingHistory(true);
    try {
      const data = await apiService.getModelValidationRuns(10);
      setRuns(data);
    } catch (error) {
      console.error('Failed to load validation runs:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleRunAll = async () => {
    setIsRunning(true);
    try {
      const run = await apiService.runModelValidation();
      setRuns(prev => [run, ...prev]);
    } catch (error) {
      console.error('Failed to run validation:', error);
      alert('Failed to run model validation. Check console for details.');
    } finally {
      setIsRunning(false);
    }
  };

  const handleRetestSingle = async (result: ModelValidationResult) => {
    const key = buildResultKey(result);
    setRetestingModel(key);
    try {
      const updated = await apiService.runSingleModelValidation({
        providerType: result.providerType,
        modelId: result.modelId,
        reasoningEffort: result.reasoningEffort,
      });
      setRuns(prev => {
        if (prev.length === 0) return prev;
        const latest = { ...prev[0] };
        latest.results = latest.results.map(r =>
          buildResultKey(r) === key ? updated : r
        );
        latest.passedCount = latest.results.filter(r => r.success).length;
        latest.failedCount = latest.results.filter(r => !r.success).length;
        return [latest, ...prev.slice(1)];
      });
    } catch (error) {
      console.error('Failed to retest model:', error);
    } finally {
      setRetestingModel(null);
    }
  };

  const handleCopyFailed = () => {
    if (!latestRun) return;
    const failed = latestRun.results.filter(r => !r.success);
    const lines = failed.map(r => {
      const effort = r.reasoningEffort ? ` [${r.reasoningEffort}]` : '';
      const err = r.errorMessage ? ` — ${r.errorMessage}` : '';
      return `${r.providerType} / ${r.modelId}${effort}${err}`;
    });
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const latestRun = runs[0] ?? null;

  return (
    <AppLayout>
      <div data-component="ModelValidationPage" className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Model Health Check</h1>
              <p className="text-gray-500 text-sm mt-1">
                Validate all configured models including reasoning effort levels for thinking models
              </p>
            </div>
            <div className="flex items-center gap-2">
              {latestRun && latestRun.failedCount > 0 && (
                <button
                  onClick={handleCopyFailed}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium border border-gray-200"
                  title="Copy all failed models to clipboard"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? 'Copied!' : 'Copy Failed'}
                </button>
              )}
              <button
                data-flow={isRunning ? 'running' : 'idle'}
                onClick={handleRunAll}
                disabled={isRunning}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Run All
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Latest run results */}
          {isLoadingHistory ? (
            <div data-flow="loading" className="text-center py-12 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading history...
            </div>
          ) : latestRun ? (
            <LatestRunView
              run={latestRun}
              retestingModel={retestingModel}
              onRetest={handleRetestSingle}
            />
          ) : (
            <div data-flow="empty" className="text-center py-12 text-gray-500">
              <p>No validation runs yet. Click "Run All" to start.</p>
            </div>
          )}

          {/* History */}
          {runs.length > 1 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Previous Runs</h2>
              <div className="space-y-2">
                {runs.slice(1).map(run => (
                  <RunSummaryRow key={run.id} run={run} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

function buildResultKey(result: ModelValidationResult): string {
  return `${result.providerType}/${result.modelId}/${result.reasoningEffort ?? ''}`;
}

function LatestRunView({
  run,
  retestingModel,
  onRetest,
}: {
  run: ModelValidationRun;
  retestingModel: string | null;
  onRetest: (result: ModelValidationResult) => void;
}) {
  // Group results by provider
  const byProvider = run.results.reduce<Record<string, ModelValidationResult[]>>((acc, r) => {
    (acc[r.providerType] ??= []).push(r);
    return acc;
  }, {});

  return (
    <div data-flow="latest-run">
      {/* Summary bar */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-sm text-gray-600">
          {new Date(run.startedAt).toLocaleString()}
        </span>
        <span className="flex items-center gap-1 text-sm font-medium text-green-700">
          <CheckCircle className="w-4 h-4" /> {run.passedCount} passed
        </span>
        <span className="flex items-center gap-1 text-sm font-medium text-red-700">
          <XCircle className="w-4 h-4" /> {run.failedCount} failed
        </span>
        <span className="text-sm text-gray-500">
          {run.totalModels} total
        </span>
      </div>

      {/* Results by provider */}
      <div className="space-y-4">
        {Object.entries(byProvider).map(([provider, results]) => (
          <ProviderSection
            key={provider}
            providerType={provider}
            results={results}
            retestingModel={retestingModel}
            onRetest={onRetest}
          />
        ))}
      </div>
    </div>
  );
}

function ProviderSection({
  providerType,
  results,
  retestingModel,
  onRetest,
}: {
  providerType: string;
  results: ModelValidationResult[];
  retestingModel: string | null;
  onRetest: (result: ModelValidationResult) => void;
}) {
  const passCount = results.filter(r => r.success).length;
  const allPass = passCount === results.length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className={`px-4 py-2 flex items-center justify-between ${allPass ? 'bg-green-50' : 'bg-red-50'}`}>
        <span className="font-medium text-gray-900">{providerType}</span>
        <span className={`text-sm font-medium ${allPass ? 'text-green-700' : 'text-red-700'}`}>
          {passCount}/{results.length} pass
        </span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            <th className="text-left px-4 py-2 font-medium text-gray-600">Model</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Effort</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Response</th>
            <th className="text-right px-4 py-2 font-medium text-gray-600">Time</th>
            <th className="text-right px-4 py-2 font-medium text-gray-600"></th>
          </tr>
        </thead>
        <tbody>
          {results.map(result => {
            const key = buildResultKey(result);
            const isRetesting = retestingModel === key;
            return (
              <tr key={key} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2">
                  <span className="font-medium text-gray-900">{result.displayName}</span>
                  <span className="text-xs text-gray-400 ml-2">{result.modelId}</span>
                </td>
                <td className="px-4 py-2">
                  {result.reasoningEffort ? (
                    <span className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      {result.reasoningEffort}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {result.success ? (
                    <span className="inline-flex items-center gap-1 text-green-700">
                      <CheckCircle className="w-3.5 h-3.5" /> Pass
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-700">
                      <XCircle className="w-3.5 h-3.5" /> Fail
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600 max-w-xs truncate">
                  {result.errorMessage ? (
                    <span className="text-red-600 text-xs" title={result.errorMessage}>
                      {result.errorMessage.length > 80
                        ? result.errorMessage.slice(0, 80) + '...'
                        : result.errorMessage}
                    </span>
                  ) : (
                    <span className="text-gray-500 text-xs">{result.response || '—'}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-gray-500">
                  {result.responseTimeMs > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {result.responseTimeMs < 1000
                        ? `${result.responseTimeMs}ms`
                        : `${(result.responseTimeMs / 1000).toFixed(1)}s`}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => onRetest(result)}
                    disabled={isRetesting}
                    className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-50"
                    title="Retest this model"
                  >
                    {isRetesting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RunSummaryRow({ run }: { run: ModelValidationRun }) {
  return (
    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded border border-gray-200">
      <span className="text-sm text-gray-600 w-44">
        {new Date(run.startedAt).toLocaleString()}
      </span>
      <span className="flex items-center gap-1 text-sm text-green-700">
        <CheckCircle className="w-3.5 h-3.5" /> {run.passedCount}
      </span>
      <span className="flex items-center gap-1 text-sm text-red-700">
        <XCircle className="w-3.5 h-3.5" /> {run.failedCount}
      </span>
      <span className="text-xs text-gray-400">/ {run.totalModels}</span>
    </div>
  );
}
